import { ImpactLensType } from '@upright/common/impact-lenses/types';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { FinalImpactScoreType } from '@upright/common/model/output/scores/types';
import { Dimension, Valence } from '@upright/common/model/types';
import { LensBoxMode } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/types';
import { LensData, LensOptions } from '@upright/ui/common/upright/core/impact-lens-profile/types';
import { LensSelectionStates } from './selection-hook';

export interface ImpactDataComponentProps {
  impact: ImpactCategoryId;
  valence: Valence;
}

export interface DimensionDataComponentProps {
  dimension: Dimension;
  valence: Valence;
  in?: boolean; // For react-transition animating
}

export enum ImpactLensProfileLayer {
  DIV,
  SVG,
}

export interface ImpactLensProfileComponent<T> {
  component: (props: T) => JSX.Element;
  layer?: ImpactLensProfileLayer;
}

export interface ImpactLens<T extends ImpactLensType, A extends LensData<T>, B extends LensOptions<T>> {
  type: T;
  ImpactDataComponent: ImpactLensProfileComponent<ImpactDataComponentProps> | undefined;
  DimensionDataComponent?: ImpactLensProfileComponent<DimensionDataComponentProps>;
  infoBoxes: {
    SvgComponent?: () => JSX.Element;
    DivComponent?: () => JSX.Element;
  };
  SourceDescription: () => JSX.Element;
  SourceTooltipContent: () => JSX.Element;
  LensTitle: () => JSX.Element;
  LensDescription: () => JSX.Element;
  LensTooltipContent: (props: { data: LensData<T> }) => JSX.Element;
  TopRightActions?: () => JSX.Element;
  AggregateResultArea?: (props: { scoreUnit: FinalImpactScoreType | undefined }) => JSX.Element;
  TotalSumsArea?: () => JSX.Element;
  AdditionalInfoArea?: () => JSX.Element;
  boxesAreaWidth?: (fullWidth: number) => number | undefined;
  boxMode?: (fullWidth: number, options: LensOptions<T>) => LensBoxMode;
  centerAreaWidth?: (fullWidth: number) => number | undefined;
  maxDataAreaWidth?: (fullWidth: number) => number;
  aggregateResultAreaHeight?: (width: number, options: LensOptions<T>) => number | undefined;
  showTotalSumsArea?: (selectionState: LensSelectionStates[T]) => boolean | undefined;
  defaultOptions?: (
    data: LensData<T>,
    state: LensSelectionStates[T],
    passedOptions: Partial<B> | undefined,
    readScoreUnitFromCommonSelector?: boolean, // OH TODO: Remove once net score back project is published
  ) => B;
  defaultSelectionState?: LensSelectionStates[T];
  EmptyState?: () => JSX.Element;
  foo?: A;

  // selection state stuff (maybe we should group this into its own attribute & interface)
  onImpactTitleClick?: (currentSelection: LensSelectionStates[T], impact: ImpactCategoryId) => LensSelectionStates[T];
  onImpactDataAreaClick?: (
    currentSelection: LensSelectionStates[T],
    impact: ImpactCategoryId,
    valence: Valence,
  ) => LensSelectionStates[T];
  isImpactTitleClickable?: (data: A, impact: ImpactCategoryId) => boolean;
  isImpactDataAreaClickable?: (data: A, impact: ImpactCategoryId, valence: Valence) => boolean;
  serializeSelectionState: (selection: LensSelectionStates[T]) => string | undefined;
  unserializeSelectionState: (value: string | undefined) => LensSelectionStates[T];
  isImpactHighlighted?: (selectionStatus: LensSelectionStates[T], impactId: ImpactCategoryId) => boolean;
  isImpactDataAreaHighlighted?: (
    selectionStatus: LensSelectionStates[T],
    impactId: ImpactCategoryId,
    valence: Valence,
  ) => boolean;
  showClearSelectionButton?: (selection: LensSelectionStates[T]) => boolean;
  getClearedSelectionState?: (state: LensSelectionStates[T]) => LensSelectionStates[T];
}

export const allImpactLenses = [
  ImpactLensType.UPRIGHT_NET_IMPACT,
  ImpactLensType.SDGS,
  ImpactLensType.EU_TAXONOMY,
  ImpactLensType.EU_PAIS,
  ImpactLensType.CSRD,
  ImpactLensType.FINANCIAL_EFFECTS,
  ImpactLensType.CLIMATE_RISK,
  ImpactLensType.INSIGHTS,
  ImpactLensType.TARGETS,
];
